/**
 * @module Helper
 */

import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { AuthService } from '@app/services/auth.service';
import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

/**
 * Interceptor for adding JWT (JSON Web Token) authorization to HTTP requests.
 *
 * This service implements the HttpInterceptor interface to intercept outgoing HTTP requests
 * and attach a JWT token to the Authorization header if the user is logged in. It relies on
 * the AuthService to manage authentication state and retrieve the token.
 *
 * @helper
 * @@Injectable
 */
@Injectable()
export class JwtInterceptor implements HttpInterceptor {
	/**
	 * Creates an instance of the JwtInterceptor.
	 *
	 * @constructor
	 * @param {AuthService} _authService - The authentication service used to check login status and retrieve the JWT token.
	 */
	constructor(private readonly _authService: AuthService) {}

	/**
	 * Intercepts an outgoing HTTP request and adds the JWT token to the headers if the user is logged in.
	 *
	 * @param {HttpRequest<any>} req - The HTTP request to intercept.
	 * @param {HttpHandler} next - The next handler in the chain to process the request.
	 * @returns {Observable<HttpEvent<any>>} An observable of the HTTP event stream.
	 */
	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (! this._authService.loggedIn) {
			return next.handle(req);
		}

		return this._authService.getTokenSilently$().pipe(
			mergeMap((token) => {
				const tokenReq = req.clone({
					setHeaders: { Authorization: `Bearer ${token}` },
				});
				return next.handle(tokenReq);
			}),
			catchError((err) => throwError(() => new Error(err)))
		);
	}
}
